<template>
    <div v-if="isEarlyAdopter" class="ql-bugreporter">
        <button
            v-if="!open"
            class="ql-bugreporter-fab"
            @click="openDialog"
            title="Report a bug (early adopters)"
            aria-label="Report a bug">
            <span class="ql-bugreporter-fab-icon" aria-hidden="true">🐞</span>
        </button>

        <div v-if="open" class="ql-bugreporter-overlay" @click.self="closeDialog">
            <div class="ql-bugreporter-panel" role="dialog" aria-label="Report a bug"
                @keydown.stop @keyup.stop @keypress.stop>
                <button class="ql-bugreporter-close" aria-label="Close" @click="closeDialog">&times;</button>

                <template v-if="state !== 'success'">
                    <h3 class="ql-bugreporter-title">Report a Bug</h3>
                    <p class="ql-bugreporter-sub">Describe the issue you encountered. A few technical details are captured automatically to help us debug.</p>

                    <label class="ql-bugreporter-label" for="ql-bug-title">Title</label>
                    <input id="ql-bug-title" v-model="title" class="ql-bugreporter-input"
                        :disabled="submitting" type="text" maxlength="200"
                        placeholder="Brief summary of the issue" />

                    <label class="ql-bugreporter-label" for="ql-bug-desc">Description</label>
                    <textarea id="ql-bug-desc" v-model="description" class="ql-bugreporter-textarea"
                        :disabled="submitting" rows="4"
                        placeholder="What happened? What did you expect?"></textarea>

                    <label class="ql-bugreporter-label" for="ql-bug-sev">Severity</label>
                    <select id="ql-bug-sev" v-model="severity" class="ql-bugreporter-select" :disabled="submitting">
                        <option v-for="opt in severities" :key="opt" :value="opt">{{ opt }}</option>
                    </select>

                    <div class="ql-bugreporter-context">
                        <div><strong>Route:</strong> {{ context.route }}</div>
                        <div><strong>Module:</strong> {{ context.module }}</div>
                        <div><strong>Browser:</strong> {{ context.browser }}</div>
                        <div><strong>OS:</strong> {{ context.os }}</div>
                    </div>

                    <p v-if="state === 'error'" class="ql-bugreporter-error">
                        Couldn't send that. <a href="#" @click.prevent="submit">Retry</a>, or email
                        <a :href="mailto">support@thecuelist.com</a>.
                    </p>

                    <div class="ql-bugreporter-actions">
                        <button class="ql-bugreporter-btn ghost" @click="closeDialog" :disabled="submitting">Cancel</button>
                        <button class="ql-bugreporter-btn primary" @click="submit" :disabled="submitting || !title.trim()">
                            {{ submitting ? 'Sending…' : 'Submit Report' }}
                        </button>
                    </div>
                </template>

                <template v-else>
                    <h3 class="ql-bugreporter-title">{{ sentOnline ? 'Thanks — report sent' : 'Thanks — report queued' }}</h3>
                    <p class="ql-bugreporter-success">
                        {{ sentOnline
                            ? 'Our team has been notified.'
                            : "You're offline — it will send automatically when you reconnect." }}
                    </p>
                    <p class="ql-bugreporter-ref">Reference: <code>{{ reportId }}</code></p>
                    <div class="ql-bugreporter-actions">
                        <button class="ql-bugreporter-btn primary" @click="closeDialog">Done</button>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { buildStamp, buildEnvironment } from '@/lib/buildStamp';

const BugReporter = {
    name: 'BugReporter',
    data: function() {
        return {
            open: false,
            state: 'idle', // idle | submitting | success | error
            title: '',
            description: '',
            severity: 'Medium',
            reportId: '',
            sentOnline: true,
            severities: ['Low', 'Medium', 'High', 'Critical']
        };
    },
    computed: {
        ...mapGetters(['isEarlyAdopter', 'projectID', 'currentVersion']),
        submitting: function() {
            return this.state === 'submitting';
        },
        mailto: function() {
            return 'mailto:support@thecuelist.com?subject=Cuelist%20bug%20report';
        },
        context: function() {
            return {
                route: (this.$route && this.$route.path) || window.location.pathname,
                module: this.moduleName(),
                browser: this.parseBrowser(navigator.userAgent),
                os: this.parseOS(navigator.userAgent),
                // Which build the reporter was on, and where. Costs the user nothing and
                // is the difference between a report we can act on and one we cannot:
                // "it broke" is unanswerable without knowing which bundle they had.
                // Environment is captured here but deliberately never displayed in the
                // app -- see the build stamp on the account page.
                build: buildStamp(),
                environment: buildEnvironment()
            };
        }
    },
    methods: {
        openDialog: function() {
            this.open = true;
            this.state = 'idle';
        },
        closeDialog: function() {
            this.open = false;
            if (this.state === 'success') {
                this.title = '';
                this.description = '';
                this.severity = 'Medium';
                this.reportId = '';
                this.state = 'idle';
            }
        },
        moduleName: function() {
            const name = (this.$route && this.$route.name) || '';
            const path = (this.$route && this.$route.path) || window.location.pathname;
            if (name === 'project' || path.indexOf('/project') === 0) return 'Editor';
            if (path.indexOf('/dashboard') === 0) return 'Dashboard';
            if (path.indexOf('/account') === 0) return 'Account';
            if (path.indexOf('/admin') === 0) return 'Admin';
            return 'General';
        },
        parseBrowser: function(ua) {
            const edge = ua.match(/Edg\/([\d.]+)/);
            if (edge) return 'Edge ' + edge[1];
            const opera = ua.match(/OPR\/([\d.]+)/);
            if (opera) return 'Opera ' + opera[1];
            const chrome = ua.match(/Chrome\/([\d.]+)/);
            if (chrome) return 'Chrome ' + chrome[1];
            const firefox = ua.match(/Firefox\/([\d.]+)/);
            if (firefox) return 'Firefox ' + firefox[1];
            const safari = ua.match(/Version\/([\d.]+).*Safari/);
            if (safari) return 'Safari ' + safari[1];
            return 'Unknown';
        },
        parseOS: function(ua) {
            const mac = ua.match(/Mac OS X ([\d_]+)/);
            if (mac) return 'macOS ' + mac[1].replace(/_/g, '.');
            if (ua.indexOf('Windows NT') >= 0) {
                const win = ua.match(/Windows NT ([\d.]+)/);
                return 'Windows ' + (win ? win[1] : '');
            }
            const ios = ua.match(/iPhone OS ([\d_]+)/) || ua.match(/CPU OS ([\d_]+)/);
            if (ios) return 'iOS ' + ios[1].replace(/_/g, '.');
            if (ua.indexOf('Android') >= 0) {
                const and = ua.match(/Android ([\d.]+)/);
                return 'Android ' + (and ? and[1] : '');
            }
            return 'Unknown';
        },
        submit: function() {
            if (!this.title.trim() || this.submitting) {
                return;
            }
            this.state = 'submitting';
            this.sentOnline = navigator.onLine !== false;
            this.$store.dispatch('submitBugReport', {
                title: this.title.trim(),
                description: this.description.trim(),
                severity: this.severity,
                module: this.context.module,
                browser: this.context.browser,
                os: this.context.os,
                build: this.context.build,
                environment: this.context.environment
            }).then((id) => {
                this.reportId = id;
                this.state = 'success';
            }).catch((e) => {
                console.error('bug report submit failed', e);
                this.state = 'error';
            });
        }
    }
};

export default BugReporter;
</script>

<style lang="scss" scoped>
/*
 * Icon-only, and stacked ABOVE the early-adopter badge (which sits at
 * bottom:12px, ~30px tall). Both live in the bottom-right corner; the label
 * was dropped because the pair took up a lot of horizontal space over the
 * script, and the tooltip plus aria-label carry the meaning.
 */
.ql-bugreporter-fab {
    position: fixed;
    bottom: 52px;
    right: 12px;
    z-index: 99998;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: #0d2744;
    color: #5ecfdf;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}
.ql-bugreporter-fab:hover {
    background-color: #123457;
}
.ql-bugreporter-fab-icon {
    font-size: 16px;
    line-height: 1;
}

.ql-bugreporter-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 99999;
    background-color: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}
.ql-bugreporter-panel {
    position: relative;
    width: 100%;
    max-width: 460px;
    max-height: 90vh;
    overflow-y: auto;
    background: #fff;
    border-radius: 10px;
    padding: 22px 24px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
}
.ql-bugreporter-close {
    position: absolute;
    top: 14px;
    right: 16px;
    border: none;
    background: transparent;
    font-size: 22px;
    line-height: 1;
    color: #888;
    cursor: pointer;
}
.ql-bugreporter-close:hover {
    color: #333;
}
.ql-bugreporter-title {
    margin: 0 0 4px;
    font-size: 20px;
    color: #0d2744;
}
.ql-bugreporter-sub {
    margin: 0 0 16px;
    font-size: 13px;
    color: #666;
    max-width: 92%;
}
.ql-bugreporter-label {
    display: block;
    margin: 14px 0 6px;
    font-size: 14px;
    font-weight: 600;
    color: #222;
}
.ql-bugreporter-input,
.ql-bugreporter-textarea,
.ql-bugreporter-select {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    font-family: inherit;
    color: #222;
    background: #fff;
}
.ql-bugreporter-textarea {
    resize: vertical;
    min-height: 92px;
}
.ql-bugreporter-select {
    appearance: auto;
}
.ql-bugreporter-context {
    margin-top: 18px;
    padding: 14px 16px;
    background: #f6f7f9;
    border-radius: 8px;
    font-size: 13px;
    line-height: 1.7;
    color: #6b7280;

    strong {
        color: #374151;
        font-weight: 600;
    }
}
.ql-bugreporter-error {
    margin: 12px 0 0;
    font-size: 13px;
    color: #b00020;
}
.ql-bugreporter-error a {
    color: #b00020;
    text-decoration: underline;
}
.ql-bugreporter-success {
    margin: 4px 0 8px;
    font-size: 14px;
    color: #333;
}
.ql-bugreporter-ref {
    font-size: 12px;
    color: #888;
}
.ql-bugreporter-ref code {
    font-size: 12px;
}
.ql-bugreporter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
}
.ql-bugreporter-btn {
    padding: 10px 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
}
.ql-bugreporter-btn.primary {
    background-color: #5ecfdf;
    color: #0d2744;
}
.ql-bugreporter-btn.primary:disabled {
    background-color: #9aa0a6;
    color: #fff;
    cursor: default;
}
.ql-bugreporter-btn.ghost {
    background: transparent;
    border-color: #ccc;
    color: #555;
}

/* Not on paper — see the matching note in EarlyAdopterBadge.vue. The root and
 * both fixed children are named explicitly so this still holds if the dialog is
 * ever moved out of the wrapper.
 */
@media print {
    .ql-bugreporter,
    .ql-bugreporter-fab,
    .ql-bugreporter-overlay,
    .ql-bugreporter-panel {
        display: none !important;
    }
}
</style>


// WEBPACK FOOTER //
// BugReporter.vue?0368730d